import React, { useMemo } from 'react'
import {
  FetchBillingResponse,
  LicensingBill,
  LicensingPayment,
  PaymentStatusKey,
  UserRoleAssignment,
} from '../../../../swagger'
import SpaceBetweenSection from '../../../Elements/SpaceBetweenSection'
import Header from '../../../Elements/Header'
import { useTranslation } from 'react-i18next'
import { useUser } from '../../../../UserContext'
import { useLocation, useNavigate, useParams } from 'react-router'
import IconTextButton, {
  IconTextButtonVariant,
} from '../../../Buttons/IconTextButton'
import PrintIcon from '@mui/icons-material/Print'
import { useAuth } from '../../../Routes/AuthProvider'
import ProgramLicensingInformation from '../ProgramLicensingInformation'
import { Box, Card, Divider, Paper, Typography, useTheme } from '@mui/material'
import { dateToSlashStringReinterpretedAsLocal } from '../../../../utils/dateUtility'
import { Payment } from '../../../Interfaces/Payment'
import BillingHistorySummaryTable, {
  BillingHistorySummaryTableVariant,
} from '../BillingHistorySummaryTable'
import { ACADEMIC_YEAR_TO_DISABLE_SHOW_LICENSING_DETAILS } from '../LicensingPaymentsCard'
import { LicensingProgramOptionsWithOwedValue } from '../../../Admin/UserAccounts/UserAccount'
import EmptyPage from '../../../Elements/EmptyPage'
import ProgramLicensingInformationAccordion from '../ProgramLicensingInformationAccordion'

interface ActorBillingViewProps {
  billingData: FetchBillingResponse
  achDiscount: string
  triggerRefetch: () => void
  userKey?: number
  amountDue?: string
  variantForBillingHistorySummaryTable?: BillingHistorySummaryTableVariant
  hasPermissionToFetchPaymentOptions?: boolean
  licensingProgramOptions?: LicensingProgramOptionsWithOwedValue[]
  /** Allow to display `BillingHistorySummaryTable` as an empty table  */
  showEmptyPage?: boolean
  showAccordions?: boolean
  isTableHeaderVisible?: boolean
  userRoles?: UserRoleAssignment[]
}

const ActorBillingView: React.FC<ActorBillingViewProps> = ({
  billingData,
  achDiscount,
  triggerRefetch,
  userKey,
  amountDue,
  variantForBillingHistorySummaryTable,
  hasPermissionToFetchPaymentOptions,
  licensingProgramOptions,
  showEmptyPage = true,
  showAccordions = false,
  isTableHeaderVisible = true,
  userRoles,
}) => {
  const { t } = useTranslation()
  const { actorKey } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { permissionAbility, userDetails } = useAuth()
  const { uniqueInactiveUserRoles, selectedActorRole } = useUser()

  const roleName = /tutor/i

  const isSelectedRoleTutor = roleName.test(selectedActorRole?.name ?? '')

  const isViewingTutorAccount =
    userRoles?.some((role) => /tutor/i.test(role.name)) ?? false

  const { spacing } = useTheme()

  // used to condition print reports button render based on actor permissions
  const canViewProgramFinancialReport = permissionAbility.can(
    'canViewProgramFinancialReport',
    'Reports'
  )
  const hasMultipleLicensingPayment = (
    licensingPaymentHistory: LicensingPayment[],
    paymentKey: number
  ): boolean => {
    let count = 0
    for (const licensingPayment of licensingPaymentHistory) {
      if (licensingPayment.paymentKey === paymentKey) {
        count++
        if (count > 1) return true // stop immediately
      }
    }
    return false
  }

  const licensingBills = billingData?.licensing?.programs ?? []

  const licensingPaymentHistory: Payment[] = useMemo(() => {
    if (!billingData?.licensing) {
      return []
    }

    return (
      billingData.licensing.history?.map((licensingHistory) => {
        const paymentHistory: Payment = {
          ...licensingHistory,
          isPaymentAcrossMultiplePrograms: hasMultipleLicensingPayment(
            billingData.licensing?.history as LicensingPayment[],
            licensingHistory.paymentKey
          ),
          paymentsMade: [
            {
              ...licensingHistory.licensingPayment,
              paymentKey: licensingHistory.licensingPayment.licensingPaymentKey,
              studentKey: -1,
            },
          ], // No student key applicable to licensing payment, but shared with enrollments
        }
        return paymentHistory
      }) ?? []
    )
  }, [billingData?.licensing])

  const disableMakeAPayment =
    !!licensingPaymentHistory &&
    licensingPaymentHistory.some((payment) => {
      return payment.paymentStatusKey === PaymentStatusKey.Err
    })

  const isViewingRoleBillingHistory =
    (uniqueInactiveUserRoles?.some(
      (userRole) => userRole.actorKey === Number(actorKey)
    ) &&
      !!location.pathname.match(/\/history/)) ||
    isSelectedRoleTutor

  const isLicensingPaymentsCSR =
    variantForBillingHistorySummaryTable ===
    BillingHistorySummaryTableVariant.LicensingPaymentsCSR

  /** *********************** Methods *********************** */
  const navigateToReports = () => {
    if (isSelectedRoleTutor) {
      navigate('/account/billing/report/tutor-licensing-fee')
      return
    }

    const currentActorKey = actorKey ?? userDetails.actingAs

    const navigateTo = isViewingRoleBillingHistory
      ? `/account/billing/history/${currentActorKey}/report`
      : `/account/billing/${currentActorKey}/report`

    navigate({
      pathname: navigateTo,
    })
  }

  const navigateToUserLicensingReport = () => {
    navigate({
      pathname: `/admin/user-accounts/${userKey}/report`,
    })
  }

  const disableShowDetails = (licensingBill: LicensingBill) => {
    return (
      licensingBill._for.semesterOneStartDate &&
      licensingBill._for.semesterOneStartDate.getFullYear() <=
        ACADEMIC_YEAR_TO_DISABLE_SHOW_LICENSING_DETAILS
    )
  }

  if (
    licensingPaymentHistory.length === 0 &&
    licensingBills.length === 0 &&
    showEmptyPage
  ) {
    return (
      <EmptyPage
        message={
          <Typography variant="subtitle1" component="p">
            {t(
              'ActorBillingView.Message.NoBillingData',
              'No billing information available.'
            )}
          </Typography>
        }
      />
    )
  }
  const printButtonVariant = isSelectedRoleTutor
    ? IconTextButtonVariant.TutorLicensingFeeReport
    : IconTextButtonVariant.LicensingFeeReport

  const printButtonVariantCSR = isViewingTutorAccount
    ? IconTextButtonVariant.TutorLicensingFeeReport
    : IconTextButtonVariant.LicensingFeeReport
  return (
    <Card aria-labelledby="licensing" component={Paper}>
      {!isLicensingPaymentsCSR && (
        <SpaceBetweenSection
          isCentered
          marginRight={5}
          marginLeft={2}
          left={
            <Header
              id="licensing"
              headerName={t('ActorBillingView.Heading.License', 'License')}
              component="h6"
            />
          }
          right={
            canViewProgramFinancialReport || isViewingRoleBillingHistory ? (
              <IconTextButton
                id="printReports"
                variant={printButtonVariant}
                startIcon={<PrintIcon />}
                onClick={navigateToReports}
              />
            ) : null
          }
        />
      )}

      {licensingBills.length > 0 &&
        licensingBills.map((licensingBill, index) => {
          return (
            <Box ml={2} mr={3} mt={3} key={licensingBill._for.programKey}>
              {index === 0 &&
                location.pathname.match(/\/admin\/user-accounts/) && (
                  <SpaceBetweenSection
                    right={
                      <IconTextButton
                        id="printUserLicensingReport"
                        variant={printButtonVariantCSR}
                        startIcon={<PrintIcon />}
                        onClick={navigateToUserLicensingReport}
                      />
                    }
                  />
                )}
              {showAccordions ? (
                <ProgramLicensingInformationAccordion
                  key={licensingBill._for.programKey}
                  licensingBill={licensingBill}
                  achDiscount={achDiscount}
                  triggerRefetch={triggerRefetch}
                  disableMakeAPayment={disableMakeAPayment}
                  isViewingRoleBillingHistory={isViewingRoleBillingHistory}
                  disableShowDetails={disableShowDetails(licensingBill)}
                  variantForBillingHistorySummaryTable={
                    variantForBillingHistorySummaryTable
                  }
                  achAllowed={billingData?.achAllowed ?? false}
                />
              ) : (
                <>
                  <ProgramLicensingInformation
                    header={`${licensingBill._for.programType} (${
                      licensingBill._for.semesterOneStartDate
                        ? dateToSlashStringReinterpretedAsLocal(
                            licensingBill._for.semesterOneStartDate as Date
                          )
                        : ''
                    }) - ${licensingBill._for.communityName}`}
                    program={licensingBill}
                    achDiscount={achDiscount ?? 0}
                    refetch={triggerRefetch}
                    disableMakeAPayment={disableMakeAPayment}
                    hideMakeAPaymentButton={isViewingRoleBillingHistory}
                    disableShowDetails={disableShowDetails(licensingBill)}
                    variant={variantForBillingHistorySummaryTable}
                    achAllowed={billingData?.achAllowed ?? false}
                    isLcd={
                      licensingBill._for.programDirectorRole ===
                      'Licensed Community Director'
                    }
                  />
                  <Divider sx={{ borderBottomWidth: spacing(0.625) }} />
                </>
              )}
            </Box>
          )
        })}
      {isTableHeaderVisible && (
        <SpaceBetweenSection
          isCentered
          marginRight={5}
          marginLeft={2}
          left={
            <Header
              id="licensingPayments"
              headerName={t(
                'ActorBillingView.Heading.LicensingPayments',
                'License Payments'
              )}
              component="h6"
            />
          }
        />
      )}

      <BillingHistorySummaryTable
        variant={
          variantForBillingHistorySummaryTable ??
          BillingHistorySummaryTableVariant.LicensingPayments
        }
        ariaLabelledBy="licensingPaymentHistory"
        paymentHistory={licensingPaymentHistory}
        amountDue={amountDue ?? '0.00'}
        userKey={userKey}
        triggerRefresh={triggerRefetch}
        hasPermissionToFetchPaymentOptions={hasPermissionToFetchPaymentOptions}
        licensingProgramOptions={licensingProgramOptions}
      />
    </Card>
  )
}

export default ActorBillingView
